/** @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';
import { useMemo } from 'react';

import { Grid } from '@coursera/cds-core';
import type { CSSProperties } from '@coursera/coursera-ui';
import { Container, StyleSheet, breakPoint, color, css } from '@coursera/coursera-ui';

import Markdown from 'bundles/cms/components/Markdown';
import type { Maybe } from 'bundles/premium-certificate/__generated__/baseContentfulTypes';
import type { ProgramDescriptionFragment } from 'bundles/premium-certificate/__generated__/contentfulTypes';

import _t from 'i18n!nls/premium-certificate';

const cuiStyles = StyleSheet.create({
  bodyRowContainer: {
    marginTop: '2.5rem',
    [`@media (max-width: ${breakPoint.md - 1}px)`]: {
      marginTop: '1.5rem',
    },
  },
});

const sectionTitleStyles = {
  fontWeight: '700',
  fontSize: '1.25rem',
  lineHeight: '1.5rem',
  fontFamily: 'inherit',
  color: `${color.primaryText}`,
  marginBottom: '1.5rem',
  [`@media (max-width: ${breakPoint.md - 1}px)`]: {
    marginBottom: '0.5rem',
  },
};

const styles: Record<string, CSSProperties> = {
  programDescriptionWrapper: (backgroundColor = '#F5F5F5', paddingBottom = 0) => ({
    backgroundColor,
    paddingBottom,
    paddingTop: '5.25rem',
    [`@media (max-width: ${breakPoint.md - 1}px)`]: {
      padding: `3rem 0.75rem ${paddingBottom}`,
    },
    '.rc-TogglableContent': {
      '&.is-safari .content .overlay': {
        display: 'block',
      },
      '.content .overlay': {
        height: '5rem',
        top: 'initial',
        zIndex: '1',
        background: 'linear-gradient(rgba(245,245,245,0),rgba(245,245,245,1) 70%)',
      },
      '.toggle-button-wrapper': {
        marginTop: '1rem',
        span: {
          fontWeight: '700',
        },
      },
    },
    '.rc-Markdown.styled a:focus:has(img)': {
      outlineOffset: '2px',
    },
    // Images will expand beyond the width of the page without this
    img: {
      '@media screen and (max-width: 530px)': {
        width: '100%',
      },
    },
  }),
  skillsList: (skillPillColor = `${color.white}`) => ({
    listStyle: 'none',
    marginTop: '1rem',
    paddingLeft: '0',
    fontSize: '0.875rem',
    lineHeight: '1.5rem',
    li: {
      backgroundColor: skillPillColor,
      borderRadius: '1.875rem',
      color: `${color.primaryText}`,
      display: 'inline-block',
      fontSize: '0.875rem',
      lineHeight: '1.5rem',
      margin: '0 0.5rem 0.5rem 0',
      padding: '0.25rem 1.125rem',
      width: 'fit-content',
      [`@media (max-width: ${breakPoint.md - 1}px)`]: {
        fontSize: '0.75rem',
        lineHeight: '1.125rem',
      },
    },
  }),
  title: {
    fontFamily: 'inherit',
    fontWeight: '700',
    fontSize: '1.5rem',
    lineHeight: '1.875rem',
    textAlign: 'center',
    color: `${color.primaryText}`,
    [`@media (max-width: ${breakPoint.md - 1}px)`]: {
      textAlign: 'left',
    },
  },
  subtitle: {
    fontSize: '1rem',
    lineHeight: '1.875rem',
    marginTop: '1.5rem',
    textAlign: 'center',
    color: `${color.primaryText}`,
    [`@media (max-width: ${breakPoint.md - 1}px)`]: {
      textAlign: 'left',
      fontSize: '0.875rem',
      lineHeight: '1.5rem',
      marginTop: '0.75rem',
    },
  },
  sectionTitle: sectionTitleStyles,
  richTextStyles: {
    h3: sectionTitleStyles,
  },
};

const SkillsList = ({
  skillsYouWillGain,
  skillPillColor,
}: {
  skillsYouWillGain?: Maybe<string>[];
  skillPillColor?: string;
}) => {
  if (!skillsYouWillGain) {
    return null;
  }
  return (
    <ul css={styles.skillsList(skillPillColor)} data-test="course-learn">
      {skillsYouWillGain.map((skill) => (
        <li key={skill}>{skill}</li>
      ))}
    </ul>
  );
};

type PropsFromCaller = {
  additionalStyles?: {
    paddingBottom?: number;
    skillPillColor?: string;
    backgroundColor?: '#ffffff' | '#F5F5F5';
  };
};

type PropsToComponent = ProgramDescriptionFragment & PropsFromCaller;

const ProgramDescription = ({
  programDescriptionTitle,
  programDescriptionSubtitle,
  additionalStyles,
  requiredBackground,
  skillsYouWillGain,
  overviewDescription,
}: PropsToComponent) => {
  const requiredBackgroundMarkdown = useMemo(() => <Markdown source={requiredBackground} />, [requiredBackground]);
  const overviewDescriptionMarkdown = useMemo(() => <Markdown source={overviewDescription} />, [overviewDescription]);

  const programDescriptionUndefined = !overviewDescription && !requiredBackground && !skillsYouWillGain;
  if (programDescriptionUndefined) {
    return null;
  }

  return (
    <div
      id="program"
      data-test="ProgramDescription"
      data-e2e="page-description"
      css={styles.programDescriptionWrapper(additionalStyles?.backgroundColor, additionalStyles?.paddingBottom)}
    >
      <Container>
        <Grid>
          <h2 css={styles.title}>{programDescriptionTitle ?? _t('Program description')}</h2>
          {programDescriptionSubtitle && (
            <div css={styles.subtitle} data-test="description-subtitle">
              {programDescriptionSubtitle}
            </div>
          )}
        </Grid>

        <Grid className={css(cuiStyles.bodyRowContainer).className}>
          {overviewDescription && (
            <div data-test="description-overview">
              <h3 css={styles.sectionTitle}>{_t('Overview')}</h3>
              {overviewDescriptionMarkdown}
            </div>
          )}
        </Grid>

        <Grid>
          {requiredBackground && (
            <div data-test="description-background">
              <h3 css={styles.sectionTitle}>{_t('Required background')}</h3>
              {requiredBackgroundMarkdown}
            </div>
          )}

          {skillsYouWillGain && (
            <div data-test="description-skills">
              <h3 css={styles.sectionTitle}>{_t('Skills you will gain')}</h3>
              <SkillsList skillsYouWillGain={skillsYouWillGain} skillPillColor={additionalStyles?.skillPillColor} />
            </div>
          )}
        </Grid>
      </Container>
    </div>
  );
};

export default ProgramDescription;
